import { useState, useEffect, useRef } from 'react';
import * as api from '../../api/index.js';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  Switch,
  IconButton,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const ApiManager = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [usersApiKeys, setUsersApiKeys] = useState([]);

  const [newKey, setNewKey] = useState({
    serviceName: 'remove_bg',
    apiKeyName: '',
    apiKey: '',
    isActive: true,
  });

  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedApiKey, setSelectedApiKey] = useState('');

  const isMountedRef = useRef(true);

  const fetchGroups = async () => {
    try {
      const response = await api.getGroups();
      const data = Array.isArray(response.data) ? response.data : [];
      if (!isMountedRef.current) return;
      setGroups(data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      if (!isMountedRef.current) return;
      setGroups([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.getUsers();
      const data = Array.isArray(response.data) ? response.data : [];
      if (!isMountedRef.current) return;
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (!isMountedRef.current) return;
      setUsers([]);
    }
  };

  const fetchUsersApiKeys = async () => {
    try {
      const response = await api.getAllUsersApiKeys();
      const data = Array.isArray(response.data) ? response.data : [];
      if (!isMountedRef.current) return;
      setUsersApiKeys(data);
    } catch (error) {
      console.error('Error fetching Users API keys:', error);
      if (!isMountedRef.current) return;
      setUsersApiKeys([]);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await api.getAllApiKeys();
      const data = Array.isArray(response.data) ? response.data : [];
      data.sort((a, b) => a.serviceName.localeCompare(b.serviceName));
      if (!isMountedRef.current) return;
      setApiKeys(data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      if (!isMountedRef.current) return;
      setApiKeys([]);
    }
  };

const handleAttachKey = async () => {
  if (!selectedGroup || !selectedUsers.length || !selectedApiKey) {
    alert('Veuillez sélectionner un groupe, au moins un utilisateur et une clé.');
    return;
  }

  try {
    for (const userName of selectedUsers) {
      await api.attachApiKeyToUser({
        groupName: selectedGroup,
        userName,
        apiKeyId: selectedApiKey,
      });
    }

    await fetchUsersApiKeys();
  } catch (error) {
    console.error('Error attaching API key to user(s):', error);
    if (error.response?.data?.error) {
      alert('Erreur: ' + error.response.data.error);
    }
  }
};

    const handleDetachKey = async (apiData, key) => {
    try {
      await api.detachApiKeyFromUser({
        groupName: apiData.groupName,
        userName: apiData.userName,
        apiKeyId: key._id,
      });

      await fetchUsersApiKeys();
    } catch (error) {
      console.error('Error detaching API key from user:', error);
      if (error.response?.data?.error) {
        alert('Erreur: ' + error.response.data.error);
      }
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    try {
      await api.createApiKey(newKey);

      setNewKey({
        serviceName: 'remove_bg',
        apiKeyName: '',
        apiKey: '',
        isActive: true,
      });

      await fetchApiKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      if (error.response?.data?.error) {
        alert('Erreur: ' + error.response.data.error);
      }
    }
  };

  const handleToggleKeyActive = async (key) => {
    const newValue = !(key.isActive === false ? false : true); // true par défaut si undefined

    try {
      await api.updateApiKeyStatus(key._id, newValue);
      // on refetch la liste pour rester simple et sûr
      await fetchApiKeys();
      await fetchUsersApiKeys();
    } catch (error) {
      console.error('Error updating API key status:', error);
      if (error.response?.data?.error) {
        alert('Erreur: ' + error.response.data.error);
      }
    }
  };

  const handleGroupChange = (e) => {
  const value = e.target.value;
  setSelectedGroup(value);
  setSelectedUsers([]); // reset du multi-select
};

const handleUsersChange = (e) => {
  const {
    target: { value },
  } = e;

  setSelectedUsers(typeof value === 'string' ? value.split(',') : value);
};

const countAssociationsForKey = (keyId) => {
  return (usersApiKeys || []).reduce((acc, apiData) => {
    const num = (apiData.apiKeys || []).filter((k) => k._id === keyId).length;
    return acc + num;
  }, 0);
};

const handleDeleteKey = async (key) => {
  const associationCount = countAssociationsForKey(key._id);

  const confirmed = window.confirm(
    `Êtes-vous sûr de vouloir supprimer cette clé :\n` +
    `- Service : ${key.serviceName}\n` +
    `- Nom : ${key.apiKeyName}\n\n` +
    `Elle est actuellement associée à ${associationCount} utilisateur(s).\n` +
    `La suppression entraînera leur désassociation.`
  );

  if (!confirmed) return;

  try {
    await api.deleteApiKey(key._id);
    await fetchApiKeys();
    await fetchUsersApiKeys();
  } catch (error) {
    console.error('Error deleting API key:', error);
    if (error.response?.data?.error) {
      alert('Erreur: ' + error.response.data.error);
    }
  }
};

  const filteredUsers = selectedGroup
    ? users.filter((u) => u.provider === selectedGroup)
    : users;

  // tri des associations par groupe puis user
  const sortedUsersApiKeys = [...(usersApiKeys || [])].sort((a, b) => {
    const g = (a.groupName || '').localeCompare(b.groupName || '');
    if (g !== 0) return g;
    return (a.userName || '').localeCompare(b.userName || '');
  });

  useEffect(() => {
    isMountedRef.current = true;

    fetchApiKeys();
    fetchUsersApiKeys();
    fetchGroups();
    fetchUsers();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        API Manager
      </Typography>

      <Grid container spacing={3}>
        {/* Colonne gauche : gestion des clés */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Gestion des clés API
            </Typography>

            {/* Formulaire de création */}
            <Box
              component="form"
              onSubmit={handleCreateKey}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}
            >
              <TextField
                label="Service name"
                value={newKey.serviceName}
                onChange={(e) =>
                  setNewKey({ ...newKey, serviceName: e.target.value })
                }
                required
                size="small"
              />

              <TextField
                label="Key name"
                value={newKey.apiKeyName}
                onChange={(e) =>
                  setNewKey({ ...newKey, apiKeyName: e.target.value })
                }
                required
                size="small"
              />

              <TextField
                label="API key"
                type="password"
                value={newKey.apiKey}
                onChange={(e) =>
                  setNewKey({ ...newKey, apiKey: e.target.value })
                }
                required
                size="small"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={newKey.isActive}
                    onChange={(e) =>
                      setNewKey({ ...newKey, isActive: e.target.checked })
                    }
                  />
                }
                label="Active"
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="submit" variant="contained">
                  Créer la clé
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Tableau des clés existantes */}
            <Typography variant="subtitle1" gutterBottom>
              Clés existantes
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Nom de la clé</TableCell>
                    <TableCell>Renseignée</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
               <TableBody>
                {(apiKeys || []).map((key) => (
                  <TableRow key={key._id}>
                    <TableCell>{key.serviceName}</TableCell>
                    <TableCell>{key.apiKeyName}</TableCell>
                    <TableCell>{key.isPresent ? 'Oui' : 'Non'}</TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={key.isActive !== false}
                        onChange={() => handleToggleKeyActive(key)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteKey(key)}
                      >
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {(!apiKeys || apiKeys.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Aucune clé pour le moment.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Colonne droite : associations users/groupes */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Association clés / groupes / utilisateurs
            </Typography>

            {/* Formulaire d'association */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mb: 3,
              }}
            >
              <FormControl size="small" fullWidth>
                <InputLabel>Groupe</InputLabel>
                <Select
                  label="Groupe"
                  value={selectedGroup}
                  onChange={handleGroupChange}
                >
                  <MenuItem value="">
                    <em>-- choisir un groupe --</em>
                  </MenuItem>
                  {(groups || []).map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth disabled={!selectedGroup}>
              <InputLabel>Utilisateurs</InputLabel>
              <Select
                label="Utilisateurs"
                multiple
                value={selectedUsers}
                onChange={handleUsersChange}
                renderValue={(selected) => (selected || []).join(', ')}
              >
                {(filteredUsers || []).map((u) => (
                  <MenuItem key={u.userName} value={u.userName}>
                    <Checkbox checked={selectedUsers.indexOf(u.userName) > -1} />
                    <Typography variant="body2">{u.userName}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Clé API</InputLabel>
                <Select
                  label="Clé API"
                  value={selectedApiKey}
                  onChange={(e) => setSelectedApiKey(e.target.value)}
                >
                  <MenuItem value="">
                    <em>-- choisir une clé --</em>
                  </MenuItem>
                  {(apiKeys || []).map((key) => (
                    <MenuItem key={key._id} value={key._id}>
                      {key.serviceName} - {key.apiKeyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleAttachKey}
                  disabled={!selectedGroup || !selectedUsers.length || !selectedApiKey}
                >
                  Associer la clé
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Tableau des associations */}
            <Typography variant="subtitle1" gutterBottom>
              Associations existantes
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Groupe</TableCell>
                    <TableCell>Utilisateur</TableCell>
                    <TableCell>Clés associées</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedUsersApiKeys.map( (apiData) => (
                    apiData.apiKeys || []).length > 0 && (
                    <TableRow key={apiData._id}>
                      <TableCell>{apiData.groupName}</TableCell>
                      <TableCell>{apiData.userName}</TableCell>
                      <TableCell>
                        {(apiData.apiKeys || []).map((k) => (
                          <Box
                            key={k._id}
                            sx={{ display: 'inline-flex', alignItems: 'center', mr: 1, mb: 0.5 }}
                          >
                            <Typography variant="body2" component="span">
                              {k.serviceName} / {k.apiKeyName} / {k.isActive ? 'Active' : 'Inactive'}
                            </Typography>
                            <IconButton
                              size="small"
                              sx={{ ml: 0.5 }}
                              onClick={() => handleDetachKey(apiData, k)}
                            >
                              <LinkOffIcon fontSize="inherit" />
                            </IconButton>
                          </Box>
                        ))}
                        {(apiData.apiKeys || []).length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Aucune clé
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    
                  ))}
                  {sortedUsersApiKeys.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        Aucune association pour le moment.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApiManager;
